import { Icon } from '@iconify/react';
import { ArrowLeftRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { CreateTokenDialog } from '@/Domain/components/Integration/ApiIntegration/CreateTokenDialog';
import { UncopiedTokenDialog } from '@/Domain/components/Integration/ApiIntegration/components/UncopiedTokenDialog';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/Domain/components/Shared/Accordion/Accordion';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { useApiTokens } from '@/Domain/hooks/Integration/useApiTokens';
import { useModalStore } from '@/Domain/store/modal.store';
import { SCENARIO_ID } from '@/context';

export const API_INTEGRATION_SHEET = 'API_INTEGRATION_SHEET';

export const ApiIntegrationSheet = () => {
  const { t } = useTranslation(['integrations']);
  const open = useModalStore(useCallback((state) => state.isOpen(API_INTEGRATION_SHEET), []));
  const closeModal = useModalStore((state) => state.closeModal);

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const {
    activeTokens,
    createToken,
    isCreating,
    revokeToken,
    isRevoking,
    updateTokenScopes,
    isUpdatingTokenScopes,
    copiedTokens,
    handleCopyToken,
    hasUncopiedPendingTokens,
    legacyInfo,
  } = useApiTokens(open);

  const [copiedLegacyFields, setCopiedLegacyFields] = useState<Set<string>>(new Set());

  const handleCopyLegacyField = useCallback(async (field: string, value: string) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        // Fallback for environments where clipboard API is not available
        const textArea = document.createElement('textarea');
        textArea.value = value;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedLegacyFields((prev) => new Set([...prev, field]));
      setTimeout(() => {
        setCopiedLegacyFields((prev) => {
          const next = new Set(prev);
          next.delete(field);
          return next;
        });
      }, 2000);
    } catch {
      // noop
    }
  }, []);

  const formatDateTime = (value: any): string => {
    if (!value) return '-';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
  };

  const getDaysUntilExpiration = (expirationTimestamp?: string): number | null => {
    if (!expirationTimestamp) return null;
    const expiration = new Date(expirationTimestamp);
    if (Number.isNaN(expiration.getTime())) return null;
    const now = new Date();
    const diffMs = expiration.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const isTokenExpired = (expirationTimestamp?: string): boolean => {
    if (!expirationTimestamp) return false;
    const expiration = new Date(expirationTimestamp);
    if (Number.isNaN(expiration.getTime())) return false;
    return expiration.getTime() < new Date().getTime();
  };

  const getExpirationText = (expirationTimestamp?: string): string => {
    const days = getDaysUntilExpiration(expirationTimestamp);
    if (days === null)
      return t('integrations:api_integration.sheet.active_tokens.expiration.never');
    if (days < 0) return t('integrations:api_integration.sheet.active_tokens.expiration.expired');
    if (days === 0) return t('integrations:api_integration.sheet.active_tokens.expiration.today');
    if (days === 1)
      return t('integrations:api_integration.sheet.active_tokens.expiration.tomorrow');
    return t('integrations:api_integration.sheet.active_tokens.expiration.days', { count: days });
  };

  const handleCreateToken = useCallback(
    (description: string, scopes: string[]) => {
      createToken({ description, scopes });
      setCreateDialogOpen(false);
    },
    [createToken],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      const currentOpen = useModalStore.getState().isOpen(API_INTEGRATION_SHEET);
      if (!currentOpen || nextOpen === currentOpen) return;

      if (!nextOpen) {
        if (hasUncopiedPendingTokens) {
          setConfirmationModalOpen(true);
        } else {
          closeModal();
        }
      }
    },
    [hasUncopiedPendingTokens, closeModal],
  );

  const handleConfirmationOpenChange = useCallback((nextOpen: boolean) => {
    const currentOpen = useModalStore.getState().isOpen(API_INTEGRATION_SHEET);
    if (!currentOpen) return;
    setConfirmationModalOpen(nextOpen);
  }, []);

  const handleConfirmClose = useCallback(() => {
    const currentOpen = useModalStore.getState().isOpen(API_INTEGRATION_SHEET);
    if (!currentOpen) return;
    setConfirmationModalOpen(false);
    closeModal();
  }, [closeModal]);

  useEffect(() => {
    if (!open) {
      setConfirmationModalOpen(false);
      setCreateDialogOpen(false);
    }
  }, [open]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const currentOpen = useModalStore.getState().isOpen(API_INTEGRATION_SHEET);
      if (!currentOpen) return;
      if (e.key === 'Escape') {
        if (confirmationModalOpen) return;
        e.preventDefault();
        e.stopPropagation();
        handleOpenChange(false);
      }
    },
    [confirmationModalOpen, handleOpenChange],
  );

  const handleNeedHelp = () => {
    window.open('https://docs.quickchat.ai/Channels/api', '_blank');
  };

  useEffect(() => {
    if (!open) return;
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [open, handleKeyDown]);

  const docLink = useMemo(
    () => (
      <a
        href="https://docs.quickchat.ai/Channels/api"
        target="_blank"
        rel="noopener noreferrer"
        className="underline font-medium hover:text-foreground"
      >
        {t('integrations:api_integration.sheet.tokens_section.description').match(
          /<0>(.*)<\/0>/,
        )?.[1] || 'API documentation'}
      </a>
    ),
    [t],
  );

  const existingDescriptions = useMemo(
    () => activeTokens.map((token) => token.description),
    [activeTokens],
  );

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="max-w-3xl w-full h-full" tabIndex={undefined}>
        <SheetHeader className="px-0.5">
          <SheetTitle data-testid="api-access-title">
            {t('integrations:api_integration.sheet.title')}
          </SheetTitle>
          <section className="flex items-center gap-2">
            <Button
              variant="link"
              onClick={handleNeedHelp}
              className="!pl-0"
              data-testid="need-help-button"
            >
              <Icon icon="mingcute:question-line" className="size-5 text-muted-foreground" />
              <span className="!text-muted-foreground text-sm">
                {t('integrations:api_integration.sheet.need_help')}
              </span>
            </Button>
            <IconButton
              onClick={() => handleOpenChange(false)}
              variant="secondary"
              data-testid="close-sheet-button"
            >
              <CloseIcon />
            </IconButton>
          </section>
        </SheetHeader>

        <div
          className="flex items-center gap-2 text-xs text-foreground-subtle p-4 bg-muted rounded-lg"
          data-testid="documentation-section"
        >
          <Icon icon="mingcute:document-2-line" className="size-4 shrink-0" />
          <p className="m-0 flex-1 leading-5">
            <Trans
              t={t}
              i18nKey="integrations:api_integration.sheet.footer_docs"
              values={{ scenarioId: SCENARIO_ID }}
              components={[
                docLink,
                <span
                  key="scenario"
                  className="font-mono font-semibold"
                  data-testid="scenario-id-display"
                />,
              ]}
            />
          </p>
        </div>

        <div className="overflow-y-auto px-0.5 space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3
                className="text-base font-semibold text-foreground"
                data-testid="api-tokens-title"
              >
                {t('integrations:api_integration.sheet.tokens_section.title')}
              </h3>
              <p
                className="text-sm text-muted-foreground mt-1"
                data-testid="api-tokens-description"
              >
                {t('integrations:api_integration.sheet.tokens_section.description')}
              </p>
            </div>
            <Button
              variant="dark"
              onClick={() => setCreateDialogOpen(true)}
              data-testid="create-token-button"
            >
              <Icon icon="mingcute:add-line" className="size-4" />
              {t('integrations:api_integration.sheet.tokens_section.create_button')}
            </Button>
          </div>

          <div className="space-y-3">
            {activeTokens.length === 0 ? (
              <div
                className="text-sm text-foreground-subtle py-8 text-center"
                data-testid="empty-tokens-message"
              >
                {t('integrations:api_integration.sheet.tokens_section.empty_message')}
              </div>
            ) : (
              <div className="space-y-3">
                {activeTokens.map((token) => {
                  const expired = isTokenExpired(token.expiration_timestamp);
                  const expirationText = getExpirationText(token.expiration_timestamp);

                  return (
                    <div
                      key={token.token_uuid}
                      data-testid={`token-card-${token.token_uuid}`}
                      className={`rounded-xl border p-4 space-y-3 ${
                        expired ? 'bg-amber-50 border-amber-300' : 'bg-card border-border'
                      }`}
                    >
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-start">
                        <div className="md:col-span-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-foreground-subtle font-normal">
                              {t(
                                'integrations:api_integration.sheet.active_tokens.card.description',
                              )}
                            </Label>
                            {token.token && (
                              <Badge variant="secondary" size="xs">
                                {t(
                                  'integrations:api_integration.sheet.active_tokens.card.badge.new',
                                )}
                              </Badge>
                            )}
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                              onClick={() => {
                                const isReadWrite = token.scopes?.includes('write_all');
                                updateTokenScopes({
                                  tokenUuid: token.token_uuid,
                                  scopes: isReadWrite ? ['read_all'] : ['read_all', 'write_all'],
                                });
                              }}
                              disabled={isUpdatingTokenScopes(token.token_uuid)}
                              data-testid={`token-scope-toggle-${token.token_uuid}`}
                              title={t(
                                'integrations:api_integration.sheet.active_tokens.card.scope_change_hint',
                              )}
                            >
                              <Badge
                                variant={
                                  token.scopes?.includes('write_all') ? 'default' : 'outline'
                                }
                                size="xs"
                                data-testid={`token-scope-badge-${token.token_uuid}`}
                              >
                                {token.scopes?.includes('write_all')
                                  ? t(
                                      'integrations:api_integration.sheet.active_tokens.card.scope_read_write',
                                    )
                                  : t(
                                      'integrations:api_integration.sheet.active_tokens.card.scope_read_only',
                                    )}
                                <ArrowLeftRight className="size-3 ml-0.5" />
                              </Badge>
                            </button>
                          </div>
                          <p
                            className="text-sm text-foreground mt-0.5 truncate"
                            data-testid={`token-description-${token.token_uuid}`}
                            title={token.description}
                          >
                            {token.description}
                          </p>
                        </div>
                        <div className="md:col-span-1">
                          <Label className="text-xs text-foreground-subtle font-normal">
                            {t('integrations:api_integration.sheet.active_tokens.card.created')}
                          </Label>
                          <p
                            className="text-sm text-foreground font-mono mt-0.5"
                            data-testid={`token-created-${token.token_uuid}`}
                          >
                            {formatDateTime(token.created_timestamp)}
                          </p>
                        </div>
                        <div className="md:col-span-1">
                          <Label className="text-xs text-foreground-subtle font-normal">
                            {t('integrations:api_integration.sheet.active_tokens.card.expires_in')}
                          </Label>
                          <p
                            className={`text-sm font-mono mt-0.5 ${expired ? 'text-amber-700 font-semibold' : 'text-foreground'}`}
                            data-testid={`token-expiration-${token.token_uuid}`}
                          >
                            {expirationText}
                          </p>
                        </div>
                        <div className="flex justify-end items-end h-full">
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => revokeToken(token.token_uuid)}
                            disabled={isRevoking(token.token_uuid)}
                            data-testid={`revoke-button-${token.token_uuid}`}
                            className="w-full md:w-auto mt-auto"
                          >
                            <Icon icon="mingcute:close-line" className="size-4" />
                            {isRevoking(token.token_uuid)
                              ? t('integrations:api_integration.sheet.active_tokens.card.revoking')
                              : t('integrations:api_integration.sheet.active_tokens.card.revoke')}
                          </Button>
                        </div>
                      </div>

                      {token.token && (
                        <div className="pt-3 border-t border-border space-y-1.5">
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-foreground-subtle font-normal">
                              {t('integrations:api_integration.sheet.active_tokens.card.token')}
                            </Label>
                            <Badge variant="warning" size="xs">
                              {t(
                                'integrations:api_integration.sheet.active_tokens.card.badge.shown_once',
                              )}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="dark"
                              size="sm"
                              onClick={() => handleCopyToken(token.token!)}
                              data-testid={`copy-token-button-${token.token_uuid}`}
                            >
                              <Icon
                                icon={
                                  copiedTokens.has(token.token)
                                    ? 'mingcute:check-fill'
                                    : 'mingcute:copy-2-line'
                                }
                                className="size-4"
                              />
                              {copiedTokens.has(token.token)
                                ? t('integrations:api_integration.sheet.active_tokens.card.copied')
                                : t('integrations:api_integration.sheet.active_tokens.card.copy')}
                            </Button>
                            <Input
                              type="text"
                              size="sm"
                              value={token.token}
                              readOnly
                              className="flex-1 font-mono text-muted-foreground text-sm"
                              data-testid={`token-value-${token.token_uuid}`}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <Separator className="my-4 w-full" />

          <Accordion type="single" collapsible className="w-full" data-testid="legacy-api-section">
            <AccordionItem value="legacy" className="border-0">
              <AccordionTrigger className="py-3 px-4 bg-amber-50 border border-amber-200 rounded-xl hover:no-underline data-[state=open]:rounded-b-none data-[state=open]:border-b-0">
                <div className="flex items-center gap-2 text-amber-800">
                  <Icon icon="mingcute:alert-line" className="size-5" />
                  <span className="text-sm font-semibold">
                    {t('integrations:api_integration.sheet.legacy_section.title')}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="bg-amber-50 border border-t-0 border-amber-200 rounded-b-xl px-4 pb-4">
                <div className="text-xs text-amber-700 space-y-2 mb-4">
                  <p>{t('integrations:api_integration.sheet.legacy_section.warning')}</p>
                  <p>
                    <Trans
                      t={t}
                      i18nKey="integrations:api_integration.sheet.legacy_section.instructions"
                      components={[<strong key="apikey" />, <strong key="scenarioid" />]}
                    />
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label
                      htmlFor="legacy-scenario-id"
                      className="text-sm font-medium text-muted-foreground"
                    >
                      {t('integrations:api_integration.sheet.legacy_section.scenario_id')}
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="legacy-scenario-id"
                        data-testid="legacy-scenario-id-input"
                        type="text"
                        size="sm"
                        value={legacyInfo?.scenario_id || ''}
                        readOnly
                        className="flex-1 !bg-muted font-mono text-sm"
                      />
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          handleCopyLegacyField('scenario_id', legacyInfo?.scenario_id || '')
                        }
                        data-testid="copy-legacy-scenario-id-button"
                        className="whitespace-nowrap"
                      >
                        <Icon
                          icon={
                            copiedLegacyFields.has('scenario_id')
                              ? 'mingcute:check-fill'
                              : 'mingcute:copy-2-line'
                          }
                          className="size-4"
                        />
                        {copiedLegacyFields.has('scenario_id')
                          ? t('integrations:api_integration.sheet.active_tokens.card.copied')
                          : t('integrations:api_integration.sheet.active_tokens.card.copy')}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="legacy-api-key"
                      className="text-sm font-medium text-muted-foreground"
                    >
                      {t('integrations:api_integration.sheet.legacy_section.api_key')}
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="legacy-api-key"
                        data-testid="legacy-api-key-input"
                        type="text"
                        size="sm"
                        value={legacyInfo?.api_key || ''}
                        readOnly
                        className="flex-1 !bg-muted font-mono text-sm"
                      />
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleCopyLegacyField('api_key', legacyInfo?.api_key || '')}
                        data-testid="copy-legacy-api-key-button"
                        className="whitespace-nowrap"
                      >
                        <Icon
                          icon={
                            copiedLegacyFields.has('api_key')
                              ? 'mingcute:check-fill'
                              : 'mingcute:copy-2-line'
                          }
                          className="size-4"
                        />
                        {copiedLegacyFields.has('api_key')
                          ? t('integrations:api_integration.sheet.active_tokens.card.copied')
                          : t('integrations:api_integration.sheet.active_tokens.card.copy')}
                      </Button>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
        <CreateTokenDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          isCreating={isCreating}
          onSubmit={handleCreateToken}
          existingDescriptions={existingDescriptions}
        />
        <UncopiedTokenDialog
          open={confirmationModalOpen}
          onOpenChange={handleConfirmationOpenChange}
          onConfirmClose={handleConfirmClose}
        />
      </SheetContent>
    </Sheet>
  );
};
